import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import type { TemplateStepInput } from '@/features/admin-care-pathways/hooks';

interface StepEditorListProps {
  readonly steps: readonly TemplateStepInput[];
  readonly onChange: (next: readonly TemplateStepInput[]) => void;
  readonly disabled?: boolean;
}

/**
 * Inline editor for the ordered step list of a care-pathway template.
 *
 * Reorder uses up/down arrows rather than HTML5 drag-and-drop — the PRD
 * called for a drag handle but no DnD primitive exists in this repo yet,
 * and the arrow UI is keyboard-accessible by default. The `sort_order`
 * sent to the BE is the array index at submit time (the hooks layer
 * recomputes from position), so the UI never has to manage it explicitly.
 */
export function StepEditorList({
  steps,
  onChange,
  disabled = false,
}: StepEditorListProps): ReactElement {
  const { t } = useTranslation();

  const update = (idx: number, patch: Partial<TemplateStepInput>): void => {
    onChange(
      steps.map((s, i) =>
        i === idx
          ? { ...s, ...patch, sortOrder: i } // sortOrder mirrors position
          : { ...s, sortOrder: i },
      ),
    );
  };

  const remove = (idx: number): void => {
    onChange(steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, sortOrder: i })));
  };

  const move = (idx: number, dir: -1 | 1): void => {
    const target = idx + dir;
    if (target < 0 || target >= steps.length) return;
    const a = steps[idx];
    const b = steps[target];
    if (a === undefined || b === undefined) return;
    const next = steps.map((s, i) => (i === idx ? b : i === target ? a : s));
    onChange(next.map((s, i) => ({ ...s, sortOrder: i })));
  };

  const add = (): void => {
    onChange([
      ...steps,
      { name: '', description: '', sortOrder: steps.length },
    ]);
  };

  return (
    <div className="space-y-2">
      {steps.length === 0 ? (
        <p className="text-[12.5px] italic text-muted-foreground">
          {t('routes.admin.carePathways.stepsEmpty')}
        </p>
      ) : (
        <ul className="space-y-2">
          {steps.map((step, idx) => (
            <li
              key={step.id ?? `new-${String(idx)}`}
              className="rounded-md border border-border bg-card p-2.5 space-y-2"
            >
              <div className="flex items-start gap-2">
                <span className="text-xs font-semibold text-muted-foreground tabular-nums pt-2 w-6">
                  {idx + 1}.
                </span>
                <div className="flex-1 space-y-1.5 min-w-0">
                  <Input
                    type="text"
                    value={step.name}
                    placeholder={t('routes.admin.carePathways.stepNamePlaceholder')}
                    onChange={(event) => {
                      update(idx, { name: event.target.value });
                    }}
                    disabled={disabled}
                    aria-label={t('routes.admin.carePathways.stepNameAriaLabel', {
                      index: idx + 1,
                    })}
                    className="h-8 text-sm"
                  />
                  <Textarea
                    value={step.description}
                    rows={2}
                    placeholder={t('routes.admin.carePathways.stepDescriptionPlaceholder')}
                    onChange={(event) => {
                      update(idx, { description: event.target.value });
                    }}
                    disabled={disabled}
                    aria-label={t('routes.admin.carePathways.stepDescriptionAriaLabel', {
                      index: idx + 1,
                    })}
                    className="text-xs"
                  />
                </div>
                <div className="flex flex-col gap-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('routes.admin.carePathways.moveStepUp')}
                    disabled={disabled || idx === 0}
                    onClick={() => {
                      move(idx, -1);
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('routes.admin.carePathways.moveStepDown')}
                    disabled={disabled || idx === steps.length - 1}
                    onClick={() => {
                      move(idx, 1);
                    }}
                    className="h-6 w-6 p-0"
                  >
                    <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    aria-label={t('routes.admin.carePathways.removeStep')}
                    disabled={disabled}
                    onClick={() => {
                      remove(idx);
                    }}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        onClick={add}
        disabled={disabled}
      >
        <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
        {t('routes.admin.carePathways.addStep')}
      </Button>
    </div>
  );
}
